/**
 * Framework-free theme store.
 *
 * This is the SINGLE source of truth for the active theme at runtime.
 * React (theme hooks), the toggle button, and any future non-React caller
 * should read/write through this module — never touch `localStorage` or
 * `document.documentElement` directly.
 *
 * The only deliberate exception is the inline FOUC script in `index.html`,
 * which has to run *before* the JS bundle loads and therefore cannot
 * `import` from here. Keep its decision logic in lockstep with
 * `resolveTheme()` below — if you change one, change the other.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const STORAGE_KEY = "theme";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";

const isTheme = (value: unknown): value is Theme =>
  value === "light" || value === "dark" || value === "system";

const readStored = (): Theme => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(raw) ? raw : "system";
  } catch {
    return "system";
  }
};

const writeStored = (theme: Theme): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode, quota, etc. — ignore. */
  }
};

/** OS color-scheme preference. */
export const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";

/** Collapses a `Theme` (including "system") into a concrete light/dark value. */
export const resolveTheme = (theme: Theme): ResolvedTheme =>
  theme === "system" ? getSystemTheme() : theme;

/** Mirrors the resolved theme onto `<html class="dark">`. Idempotent. */
const applyToDOM = (resolved: ResolvedTheme): void => {
  document.documentElement.classList.toggle("dark", resolved === "dark");
};

const listeners = new Set<() => void>();
let mqUnsubscribe: (() => void) | null = null;

const ensureSystemListener = (): void => {
  if (mqUnsubscribe) return;
  const mq = window.matchMedia(MEDIA_QUERY);
  const handler = () => {
    if (readStored() === "system") {
      applyToDOM(getSystemTheme());
      listeners.forEach((l) => l());
    }
  };
  mq.addEventListener("change", handler);
  mqUnsubscribe = () => mq.removeEventListener("change", handler);
};

const notify = (): void => listeners.forEach((l) => l());

/** Current persisted preference. */
export const getTheme = (): Theme => readStored();

/** What's actually painted on screen right now. */
export const getResolvedTheme = (): ResolvedTheme => resolveTheme(readStored());

/** Update the preference, persist it, paint it, notify subscribers. */
export const setTheme = (next: Theme): void => {
  writeStored(next);
  applyToDOM(resolveTheme(next));
  notify();
};

/** Flip the *currently resolved* theme to its opposite. */
export const toggleTheme = (): void => {
  setTheme(getResolvedTheme() === "dark" ? "light" : "dark");
};

/**
 * Subscribe to preference changes (writes via setTheme/toggleTheme) and to
 * OS-level changes when the preference is "system". Returns an unsubscribe.
 * Designed to plug straight into React's `useSyncExternalStore`.
 */
export const subscribe = (listener: () => void): (() => void) => {
  ensureSystemListener();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && mqUnsubscribe) {
      mqUnsubscribe();
      mqUnsubscribe = null;
    }
  };
};

/**
 * Reconcile the DOM with the persisted preference. Called once at app boot
 * as a safety net — in practice the inline script in `index.html` will have
 * already done this before React mounts.
 */
export const hydrate = (): void => {
  applyToDOM(resolveTheme(readStored()));
};
